import { useEffect, useMemo, useState, useRef } from 'react';
import {
  differenceInCalendarDays,
  isAfter,
  isSameDay,
  startOfDay,
} from 'date-fns';
import { makeDateList } from '../../../../utils/appointments/dateFilter';

const DAY_WIDTH = 83;

const SAFETY_PADDING_PX = 10;

export function useDayPicker(
  activeDate,
  { days = 14, ITEMS_TO_DISPLAY = 6 } = {},
) {
  const daysBoxRef = useRef(null);

  const [offset, setOffset] = useState(0);
  const [itemsToDisplay, setItemsToDisplay] = useState(ITEMS_TO_DISPLAY);

  const options = useMemo(() => {
    const today = startOfDay(new Date());
    const activeDateObj = activeDate ? new Date(activeDate) : null;
    const daysNeeded =
      activeDateObj && isAfter(activeDateObj, today)
        ? Math.max(
            days,
            differenceInCalendarDays(activeDateObj, today) +
              Math.ceil(itemsToDisplay),
          )
        : days;
    const dateList = makeDateList(daysNeeded, null);
    const timestampList = dateList.map(({ timestamp }) => timestamp);
    const MAX_WIDTH = DAY_WIDTH * dateList.length;
    const LIMIT_NEXT = itemsToDisplay * DAY_WIDTH - MAX_WIDTH;

    return {
      dateList,
      timestampList,
      MAX_WIDTH,
      LIMIT_NEXT,
    };
  }, [days, itemsToDisplay, activeDate]);

  const disabledNext = offset <= options.LIMIT_NEXT;
  const disabledPrev = offset >= 0;

  const handleNext = () =>
    setOffset(prev => {
      const coord = prev - DAY_WIDTH;

      if (coord - SAFETY_PADDING_PX <= options.LIMIT_NEXT) {
        return options.LIMIT_NEXT;
      }

      return coord;
    });

  const handleBack = () => {
    setOffset(prev => {
      const coord = prev + DAY_WIDTH;

      if (coord + SAFETY_PADDING_PX >= 0) {
        return 0;
      }

      return coord;
    });
  };

  useEffect(() => {
    if (!activeDate) return;

    const activeIndex = options.timestampList.findIndex(ts =>
      isSameDay(new Date(ts), new Date(activeDate)),
    );
    if (activeIndex < 0) return;

    const newOffset = -activeIndex * DAY_WIDTH;
    setOffset(Math.max(options.LIMIT_NEXT, Math.min(0, newOffset)));
  }, [activeDate, itemsToDisplay]);

  useEffect(() => {
    if (daysBoxRef.current) {
      const rect = daysBoxRef.current.getBoundingClientRect();

      const { width } = rect;

      const newItemsToDisplay = width / DAY_WIDTH;
      setItemsToDisplay(newItemsToDisplay);
    }
  }, [activeDate]);

  return {
    offset,
    options,
    handleNext,
    handleBack,
    disabledNext,
    disabledPrev,
    daysBoxRef,
  };
}



// WEBPACK FOOTER //
// ./src/components/AppointmentForm/Components/DayPicker/useDayPicker.js